import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  ViewChild,
  viewChild
} from '@angular/core';
import { DealOption, DealOptionProduct } from '../../../../models/deal-options-view.models';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { DealAddendumProductPreviewComponent } from '../deal-addendum-products/deal-addendum-product-preview/deal-addendum-product-preview.component';
import { AddOptionModalComponent } from '../../../deal-detail/options/add-option-modal/add-option-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AbilityService, NgbModalService } from '@zayan-erp/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of } from 'rxjs';
import { DsmRoomReplacementRulesService } from '@zayan-erp/dsm';
type DealOptionProductWithExpandableView = DealOptionProduct & {
  isExpanded: boolean;
};
@Component({
  selector: 'deal-addendum-options',
  imports: [AccordionModule, DealAddendumProductPreviewComponent],
  providers: [],
  templateUrl: './deal-addendum-options.component.html',
  styleUrl: './deal-addendum-options.component.scss'
})
export class DealAddendumOptionsComponent implements OnDestroy {
  readonly modal = inject(NgbModal);
  readonly modalService = inject(NgbModalService);
  readonly destroyRef = inject(DestroyRef);
  private readonly roomReplacementRulesService = inject(DsmRoomReplacementRulesService);
  readonly abilityService = inject(AbilityService);

  @ViewChild('addOptionModal') addOptionModalEl!: ElementRef;

  readonly dealAddendum = input.required<DealOption>();
  readonly modelId = input.required<string>();
  readonly elevationId = input.required<string>();
  readonly selectedProductId = input<string | null>(null);
  /** Set when the deal itself is cancelled: its addendums are read-only, exactly as a locked one is. */
  readonly dealCancelled = input<boolean>(false);
  readonly isLocked = computed(
    () => this.dealAddendum().isLocked || this.dealAddendum().isCancelled || this.dealCancelled()
  );
  /** Single source of truth for this component's write gate. */
  readonly canWriteDeals = computed(() => this.abilityService.ability.can('write-sales-deals', 'deals'));

  onChanges = output<void>();
  onProductSelected = output<string>();
  onKitProductSelected = output<string>();

  readonly products = computed<DealOptionProductWithExpandableView[]>(() =>
    this.dealAddendum().products.map(
      product => ({ ...product, isExpanded: false }) as DealOptionProductWithExpandableView
    )
  );

  onProductSelect(productId: string) {
    this.onProductSelected.emit(productId);
  }

  onKitProductSelect(productId: string) {
    this.onKitProductSelected.emit(productId);
  }

  openAddOptionModal(): void {
    if (this.isLocked() || !this.canWriteDeals()) {
      return;
    }

    const modalRef = this.modal.open(AddOptionModalComponent, { ...this.modalService.centerModal, size: 'xxl' });
    const addedProductIds = this.products().map(product => product.productId);
    modalRef.componentInstance.setContext(this.modelId(), this.elevationId(), this.dealAddendum().id, addedProductIds);

    const subscription = modalRef.componentInstance.submitted.subscribe((newlyAddedProductIds: string[]) => {
      this.onChanges.emit();
      this.checkForPlanChangingOption(newlyAddedProductIds);
    });

    modalRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      subscription.unsubscribe();
    });
  }

  readonly planChangeWarningMessage = signal<string | null>(null);
  readonly planChangeWarningModal = viewChild<TemplateRef<unknown>>('planChangeWarningModal');
  #planChangeWarningModalRef: NgbModalRef | null = null;

  private checkForPlanChangingOption(productIds: string[]): void {
    if (!productIds.length) return;

    forkJoin(
      productIds.map(productId =>
        this.roomReplacementRulesService
          .validateRule(this.modelId(), this.elevationId(), productId, false)
          .pipe(catchError(() => of(null)))
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: validations => {
          const trigger = validations.find(validation => !!validation?.ruleId);
          if (trigger) {
            this.planChangeWarningMessage.set(
              'An active room replacement rule exists for this floor plan on selected model and elevation.'
            );
          }
        }
      });
  }

  private readonly openPlanChangeWarningModal = effect(() => {
    const message = this.planChangeWarningMessage();
    if (!message || this.#planChangeWarningModalRef) return;
    const template = this.planChangeWarningModal();
    if (!template) return;
    this.#planChangeWarningModalRef = this.modal.open(template, this.modalService.confirmationModal);
  });

  acknowledgePlanChangeWarning(): void {
    this.planChangeWarningMessage.set(null);
    this.#planChangeWarningModalRef?.close();
    this.#planChangeWarningModalRef = null;
  }

  ngOnDestroy(): void {
    this.#planChangeWarningModalRef?.close();
  }
}
